export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginSuccess {
  token: string;
  token_type: string;
  expires_in: number;
}

export enum UserRole {
  user = "user",
  admin = "admin",
}

export interface UserProfile {
  name: string;
  email: string;
  role: UserRole;
}

export interface TermAttachment {
  id: number;
  filename: string;
  url?: string;
  file?: string;
  term_id?: number;
}

export interface Term {
  id: number;
  title: string;
  content: string;
  attachments?: TermAttachment[];
  removedAttachments?: number[];
  sections?: Section[];
}

export enum QuestionType {
  SELECT = "select",
  MULTI_SELECT = "multiple_select",
  CHECKBOX = "checkbox",
  CHECKBOX_WITH_MAP = "checkbox_with_map",
  TEXT = "text",
  TEXT_NUMBER = "text_number",
  GROUP = "group",
  REPEATER = "repeater",
}

export interface QuestionElement {
  title: string;
  items: Question[];
}

export interface QuestionConfig {
  max?: number;
  min?: number;
  type?: string;
  pattern?: RegExp;
  placeholder?: string;
  required?: boolean;
  show_on?: { [Key: string]: string };
  allow_insert?: boolean;
  selectall?: boolean;
  unique?: boolean;
  readonly?: boolean;
  bind?: string[];
  bind_inner?: string;
  item_name?: string;
  insert_type?: string;
  details: {
    title: string;
  };
}

export interface Question {
  id: string;
  page: number;
  index?: number;
  subIndex?: number;
  section?: number;
  title: string;
  button_label?: string;
  subtitle?: string;
  type: QuestionType;
  options: string | string[] | { [Key: string]: string[] };
  conf?: QuestionConfig;
  items?: Question[];
  elements?: QuestionElement;
}

export interface Answer {
  id: string;
  answer: any;
}

export enum SurveyType {
  GOVERNANCE = "governance",
  MACRO_PRODUCT = "macroproduct",
  PRODUCT = "product",
  ATTRACTION = "attraction",
  ANALYSIS_ATTRACTION = "analysis-attraction",
  ANALYSIS_OLDTOWN = "analysis-oldtown",
  MARKET = "market",
  STRATEGY_ATTRACTION = "strategy-attraction",
  INITIATIVE_ATTRACTION = "initiative-attraction",
  ANALYSIS_ACCESSIBILITY = "analysis-accessibility",
  STRATEGY_ACCESSIBILITY = "strategy-accessibility",
  INITIATIVE_ACCESSIBILITY = "initiative-accessibility",
  ANALYSIS_HOSPITALITY = "analysis-hospitality",
  INITIATIVE_HOSPITALITY = "initiative-hospitality",
  STRATEGY_HOSPITALITY = "strategy-hospitality",
  INFOPOINT = "infopoint",
  BRAND = "brand",
  ANALYSIS_PLANNING = "analysis-planning",
  ANALYSIS_POSITIONING = "analysis-positioning",
  STRATEGY_AWARENESS = "strategy-awareness",
  INITIATIVE_AWARENESS = "initiative-awareness",
  STRATEGY_VISIBILITY = "strategy-visibility",
  INITIATIVE_VISIBILITY = "initiative-visibility",
  INITIATIVE_VALIDATED = "initiative-validated",
  USER_STRATEGY = "user-strategy",
  MERGE_MACROPRODUCTS = "merge-macroproducts",
}

export enum SurveyActionType {
  NEW = "new",
  UPDATE = "update",
}

export interface GetSurveyResult {
  breadcrumb: Array<{ [Key: string]: string }>;
  sections: SurveySection[];
  answers?: Answer[];
  dictionaries?: { [Key: string]: { [Key: string]: { [Key: string]: string } } };
}

export interface SurveySection {
  title?: string;
  subtitle?: string;
  questions: Question[];
}

export interface Section {
  name: string;
  nicename: string;
}

export enum SurveyStatusType {
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  TO_BE_UPDATED = "to_be_updated",
}

export interface SurveyStatusResult {
  expire_time: string;
  sections: SurveyStatusSection[];
  updated_at: string;
  has_user_strategy: number;
  planning_years: number[];
  last_dmp?: string;
  last_submit_time?: string;
}

export interface SurveyStatusSection {
  title: string;
  elements: SurveyStatus[];
}

export interface SurveyStatusDetails {
  to_be_completed: string;
  total: string;
}

export interface SurveyStatus {
  id?: number;
  percentage: number;
  sectionName: string;
  status: SurveyStatusType;
  details?: SurveyStatusDetails;
  survey?: string;
}

export enum MacroProductType {
  PUBLIC = "public",
  PRIVATE = "private",
  WAITING_APPROVAL = "waiting_approval",
}

export interface Pivot {
  created_at: string;
  survey_id: number;
  tptobject_id: number;
  updated_at: string;
}

export interface MacroProductSurvey {
  id: number;
  percentage: number;
  section_name: string;
  status: SurveyStatusType;
  timeframe: string;
  user_id: number;
  pivot: Pivot;
  updated_at?: string;
}

export interface MacroProduct {
  data: string;
  id: number;
  name: string;
  status: MacroProductType;
  surveys: MacroProductSurvey[];
  timeframe: string;
  type: string;
  user: UserProfile;
  user_id: number;
}

export interface Product {
  data: string;
  id: number;
  name: string;
  status: MacroProductType;
  surveys: MacroProductSurvey[];
  timeframe: string;
  type: string;
  user_id: number;
}

export interface Attractor extends Product {
  prova: string;
}

export interface AddressComponent {
  log_name: string;
  short_name: string;
  types: string[];
}

export interface GeometryLocation {
  lat: number;
  lng: number;
}

export interface GeometryViewport {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface Geometry {
  location: GeometryLocation | null;
  viewport: GeometryViewport | null;
}

export interface TPTPlaceResult {
  id?: number;
  address_components?: AddressComponent[];
  formatted_address?: string;
  geometry?: Geometry;
  name?: string;
  place_id?: string;
  reference?: string;
  types?: string[];
  url?: string;
  vicinity?: string;
  disabled?: boolean;
}

export enum ProductMarketMatrixLevel {
  DISCRETO = "Discreto",
  ELEVATO = "Elevato",
}

export interface ProductMarketMatrix {
  prodotto: string;
  mercato: string;
  livello_consenso: ProductMarketMatrixLevel;
  livello_priorita: ProductMarketMatrixLevel;
  order: number;
}

export interface InitiativeStrategyMatrix {
  title: string;
  goals: InitiativeStrategyMatrixGoal[];
}

export interface InitiativeStrategyMatrixGoal {
  category: string;
  count: string;
  list: InitiativeStrategyMatrixGoalList[];
}

export enum IniziativeStatusType {
  PUBLIC = "public",
  IN_PROGRESS = "waiting_approval",
}

export interface OperationPlansSentElement {
  name: string;
  updated_at?: string;
  url?: string;
}

export interface ValidationIniziativeMatrix {
  title: string;
  initiatives: ValidationIniziativeMatrixElement[];
}

export interface ValidationIniziativeMatrixElement {
  id: number;
  main_survey_id: number;
  cat: string;
  name: string;
  status: IniziativeStatusType;
  surveys: ValidationIniziativeMatrixElementSurvey[];
}

export interface ValidationIniziativeMatrixElementSurvey {
  id: number;
  timeframe: string;
  section_name: string;
  user_id: number;
  percentage: number;
  status: SurveyStatusType;
  updated_at: string;
  label?: string;
}

export interface InitiativeStrategyMatrixGoalList {
  name: string;
  description: string;
}

export interface Report {
  id: number;
  name: string;
  path: string;
  updated_at: string;
  url: string;
}

export interface Document {
  id: number;
  name: string;
  attachments: DocumentAttachment[];
  order?: number;
  fixed?: boolean;
  updated_at?: string;
  path?: string;
  url?: string;
}

export interface DocumentAttachment {
  id: number;
  filename: string;
  file: string;
}

export interface ProductDetails {
  content: string;
  label: string;
}

export interface LibraryGroup {
  name: string;
  nicename: string;
}

export interface LibraryTerm {
  id: number;
  name: string;
  parent_id: number;
  count: number;
}

export interface LibraryParent {
  id: number;
  name: string;
}

export interface LibraryPermissions {
  add: number;
  edit: number;
  delete: number;
}

export interface Library {
  terms: LibraryTerm[];
  parents: LibraryParent[];
  can: LibraryPermissions;
}

export interface LibraryTermRequest {
  name: string;
  library: string;
  parent_id?: number;
}

export interface StatusUserStrategy {
  updated_at: string;
  "user-strategy": string;
}

export interface User {
  id: number;
  name: string;
}

export interface UsersResult {
  users: User[];
}

export interface SingleUserResult {
  status: SurveyStatusResult;
  user: UserProfile;
}
